import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/forms/index.js).
export const FORM_COLUMNS = [
  { area: 'name', label: 'Form', width: 'minmax(220px, 1fr)', bone: 'lines' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const FORM_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['name actions'],
};

export const FORM_MIN_TABLE_WIDTH = 360;

// The forms list (Settings > Forms): each form opens its questions; delete
// asks first, since it removes what was recorded with the form.
@inject('formStore', 'viewStore') @observer
class FormsTable extends Component {
  @observable deleting = null; // the form waiting for delete confirmation

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.formSearch = { ...viewStore.formSearch, page: newPage };
    viewStore.forms();
  }

  // Empty list's "Clear search": back to every form, page 1.
  clearSearch = () => {
    const { viewStore } = this.props;
    viewStore.formSearch = { ...viewStore.formSearch, term: '', page: 1 };
    if (viewStore.bumpListFilters) viewStore.bumpListFilters();
    viewStore.forms();
  };

  confirmDelete = () => {
    const form = this.deleting;
    this.deleting = null;
    this.props.formStore.deleteForm(form)
      .then(() => {
        Toast.show({ message: `${form.attributes.name} deleted.`, intent: Intent.SUCCESS });
      })
      .catch(() => {
        Toast.show({ message: `Couldn't delete ${form.attributes.name}. Try again.`, intent: Intent.DANGER });
      });
  };

  render() {
    const { viewStore, onAdd } = this.props;
    const { objects, meta = {} } = this.props.formStore.forms;
    const term = viewStore.formSearch.term && viewStore.formSearch.term !== '*' ? viewStore.formSearch.term : '';
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="forms"
          term={term}
          onClearFilters={this.clearSearch}
          message={term ? undefined : 'Forms are checklists and questionnaires your team fills in on a job. Add one, then add its questions.'}
          extraAction={!term && onAdd && { label: 'Add form', icon: 'add', onClick: onAdd, primary: true }}
        />
      );
    }
    const deleting = this.deleting && this.deleting.attributes;
    return (
      <React.Fragment>
        <DataList.List
          columns={FORM_COLUMNS}
          cards={FORM_CARDS}
          minTableWidth={FORM_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="forms"
          note={DataList.sortNote(viewStore.formSearch.order)}
        >
          {objects.map((form) => {
            const { name, id } = form.attributes;
            return (
              <DataList.Row key={form.id}>
                <DataList.Cell area="name">
                  <DataList.RowLink href={`/dashboard/forms/${form.id}`} label={`Form ${name}`}>
                    <DataList.Lines primary={name} secondary={id ? `Form ${id}` : undefined} />
                  </DataList.RowLink>
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: `Edit ${name}`, onClick: () => viewStore.formShow({ uuid: form.id }) },
                      { icon: 'trash', label: `Delete ${name}`, intent: Intent.DANGER, onClick: () => { this.deleting = form; } },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {meta.total_pages > 1 && (
          <Footer>
            <div className="bp3-navbar-group bp3-align-left">
              <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
            </div>
          </Footer>
        )}
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete form"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>{deleting ? `Delete ${deleting.name}?` : 'Delete this form?'}</strong>
          <p>Everything linked to it, such as work orders, equipment, timesheet entries and jobs, is deleted too.</p>
        </Alert>
      </React.Fragment>
    );
  }
}

export default FormsTable;
